import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { Employee, PayrollRecord } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}

// ── Tax Constants (2026) ─────────────────────────────────────────────────────
const SS_RATE        = 0.062;
const MEDICARE_RATE  = 0.0145;
const PFML_EMP_RATE  = 0.006716;
const PFML_EMP_RATE2 = 0.002484; // employer share
const SS_WAGE_BASE   = 176100;

function federalWithholding(annualized: number): number {
  if (annualized <= 14600)  return 0;
  if (annualized <= 44725)  return (annualized - 14600) * 0.12;
  if (annualized <= 95375)  return 3615   + (annualized - 44725)  * 0.22;
  if (annualized <= 201050) return 14793  + (annualized - 95375)  * 0.24;
  if (annualized <= 383900) return 40219  + (annualized - 201050) * 0.32;
  if (annualized <= 487450) return 98671  + (annualized - 383900) * 0.35;
  return 105157.5 + (annualized - 487450) * 0.37;
}

interface TaxCalc {
  grossPay: number; federalIncomeTax: number; socialSecurity: number;
  medicare: number; waPfml: number; totalDeductions: number; netPay: number;
  employerSS: number; employerMedicare: number; employerPfml: number; employerTotal: number;
}

export function calculateTaxes(grossPay: number, ytdGross: number, payPeriodsPerYear: number): TaxCalc {
  const annualized    = grossPay * payPeriodsPerYear;
  const fedAnnual     = federalWithholding(annualized);
  const federalIncomeTax = fedAnnual / payPeriodsPerYear;
  const ssCapped      = Math.max(0, Math.min(grossPay, SS_WAGE_BASE - ytdGross));
  const socialSecurity   = ssCapped * SS_RATE;
  const medicare         = grossPay * MEDICARE_RATE;
  const waPfml           = grossPay * PFML_EMP_RATE;
  const totalDeductions  = federalIncomeTax + socialSecurity + medicare + waPfml;
  const netPay           = Math.max(0, grossPay - totalDeductions);
  const employerSS       = ssCapped * SS_RATE;
  const employerMedicare = grossPay * MEDICARE_RATE;
  const employerPfml     = grossPay * PFML_EMP_RATE2;
  const employerTotal    = employerSS + employerMedicare + employerPfml;
  return { grossPay, federalIncomeTax, socialSecurity, medicare, waPfml,
           totalDeductions, netPay, employerSS, employerMedicare, employerPfml, employerTotal };
}

// ── Component ─────────────────────────────────────────────────────────────────

export async function renderPayroll(container: HTMLElement, _navigate: NavigateFn): Promise<void> {
  const session    = getSession();
  const businessId = session?.businessId ?? 'preview';
  const industry   = session?.industry   ?? 'general';
  const hasTips    = industry !== 'construction';

  container.innerHTML = `
    <div class="page-header">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:10px">
        <div>
          <h1 class="page-title"><i class="bi bi-cash-stack"></i> Payroll Processing</h1>
          <p class="page-subtitle">WA State + Federal deductions auto-calculated. No state income tax. No Bellingham city tax.</p>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          <button class="btn btn-ghost" id="close-quarter-btn"><i class="bi bi-calendar3"></i> Close Quarter</button>
          <button class="btn btn-ghost" id="close-year-btn"><i class="bi bi-calendar-check"></i> Year-End Close</button>
          <button class="btn btn-gold" id="run-payroll-btn"><i class="bi bi-plus-circle"></i> Run Payroll</button>
        </div>
      </div>
    </div>

    <div style="background:var(--bg-card);border:1px solid var(--border);border-left:3px solid var(--brand-blue-light);border-radius:8px;padding:11px 16px;margin-bottom:20px;font-size:12px;color:var(--text-muted)">
      <strong style="color:var(--brand-blue-light)">2026 Rates:</strong>
      SS 6.2% (cap $176,100) · Medicare 1.45% · WA PFML 0.67% employee / 0.25% employer · No WA state income tax · No Bellingham city payroll tax
    </div>

    <!-- ── Run Payroll Modal ──────────────────────────────────────────────── -->
    <div id="payroll-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.65);z-index:1000;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto">
      <div class="card" style="width:660px;margin-bottom:40px">
        <h3 style="font-size:15px;font-weight:700;margin-bottom:16px">Run Payroll</h3>

        <div class="form-row-2">
          <div>
            <label>Employee *</label>
            <select id="pay-employee-id"><option value="">— Select Employee —</option></select>
          </div>
          <div>
            <label>Pay Frequency</label>
            <select id="pay-period-type">
              <option value="26">Biweekly (26×/yr)</option>
              <option value="24">Semi-monthly (24×/yr)</option>
              <option value="52">Weekly (52×/yr)</option>
              <option value="12">Monthly (12×/yr)</option>
            </select>
          </div>
        </div>

        <div class="form-row-2">
          <div><label>Pay Period Start *</label><input type="date" id="pay-period-start" /></div>
          <div><label>Pay Period End *</label><input type="date" id="pay-period-end" /></div>
        </div>

        <div class="form-row-2">
          <div><label>Regular Hours *</label><input type="number" id="pay-hours" min="0" max="999" step="0.5" placeholder="80" /></div>
          <div><label>Overtime Hours (1.5×)</label><input type="number" id="pay-ot-hours" min="0" max="999" step="0.5" placeholder="0" /></div>
        </div>

        <div class="form-row-2">
          <div><label>Hourly Rate ($) *</label><input type="number" id="pay-rate" min="0" step="0.01" placeholder="auto-filled from employee" /></div>
          <div><label>YTD Gross (prior periods, for SS cap)</label><input type="number" id="pay-ytd" min="0" step="0.01" placeholder="0.00" /></div>
        </div>

        ${hasTips ? `<div class="form-row"><label>Tips Received ($)</label><input type="number" id="pay-tips" min="0" step="0.01" placeholder="0.00" /></div>` : ''}

        <!-- Live Tax Breakdown (always visible) -->
        <div style="background:var(--bg-tertiary);border-radius:8px;padding:16px;margin:12px 0;font-size:13px">
          <div style="font-weight:700;margin-bottom:10px;color:var(--text-secondary);display:flex;align-items:center;gap:6px">
            <i class="bi bi-calculator"></i> Live Pay Preview
            <span id="preview-notice" style="font-size:11px;color:var(--text-muted);font-weight:400">(fill in hours and rate to calculate)</span>
          </div>
          <div style="display:grid;grid-template-columns:1fr auto;gap:5px 16px;font-size:13px">
            <span style="color:var(--text-muted)">Regular Pay</span>     <span id="prev-reg" style="text-align:right">—</span>
            <span style="color:var(--text-muted)">Overtime Pay</span>    <span id="prev-ot"  style="text-align:right">—</span>
            ${hasTips ? `<span style="color:var(--text-muted)">Tips</span><span id="prev-tips-amt" style="text-align:right">—</span>` : ''}
            <span style="color:var(--text-muted);font-weight:600">Gross Pay</span>
            <span id="prev-gross" style="text-align:right;font-weight:700"></span>
            <div style="grid-column:1/-1;border-top:1px solid var(--border);margin:4px 0"></div>
            <span style="color:var(--text-muted)">Federal Income Tax</span><span id="prev-fed"  style="text-align:right;color:var(--danger)">—</span>
            <span style="color:var(--text-muted)">Social Security (6.2%)</span><span id="prev-ss"   style="text-align:right;color:var(--danger)">—</span>
            <span style="color:var(--text-muted)">Medicare (1.45%)</span>   <span id="prev-med"  style="text-align:right;color:var(--danger)">—</span>
            <span style="color:var(--text-muted)">WA PFML (0.67%)</span>    <span id="prev-pfml" style="text-align:right;color:var(--danger)">—</span>
            <div style="grid-column:1/-1;border-top:1px solid var(--border);margin:4px 0"></div>
            <span style="font-weight:700;font-size:14px">Net Pay</span>
            <span id="prev-net" style="text-align:right;font-weight:800;font-size:16px;color:var(--success)">—</span>
          </div>
          <div style="margin-top:10px;font-size:11px;color:var(--text-muted);border-top:1px solid var(--border);padding-top:8px">
            Employer cost: +SS $<span id="prev-emp-ss">—</span> · +Medicare $<span id="prev-emp-med">—</span> · +PFML $<span id="prev-emp-pfml">—</span> = <strong>$<span id="prev-emp-total">—</span></strong>
          </div>
        </div>

        <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:8px;padding:10px 14px;margin-bottom:12px;font-size:12px;color:var(--text-muted)">
          <i class="bi bi-bank" style="color:var(--brand-gold)"></i>
          <strong style="color:var(--brand-gold)"> Bank Wire</strong> — Net pay wired from your business account.
          <span id="bank-wire-status"></span>
        </div>

        <div style="display:flex;gap:10px;justify-content:flex-end">
          <button class="btn btn-ghost" id="close-payroll-modal">Cancel</button>
          <button class="btn btn-gold" id="save-payroll-btn"><i class="bi bi-check-circle"></i> Process Payroll</button>
        </div>
      </div>
    </div>

    <!-- ── Print Stub Modal ───────────────────────────────────────────────── -->
    <div id="stub-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1001;align-items:center;justify-content:center">
      <div class="card" style="width:520px;max-height:90vh;overflow-y:auto">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
          <h3 style="font-size:15px;font-weight:700">Pay Stub</h3>
          <div style="display:flex;gap:8px">
            <button class="btn btn-ghost btn-sm" id="stub-print-btn"><i class="bi bi-printer"></i> Print</button>
            <button class="btn btn-ghost btn-sm" id="stub-close-btn"><i class="bi bi-x"></i></button>
          </div>
        </div>
        <div class="form-row" style="margin-bottom:16px">
          <label>Select Pay Period</label>
          <select id="stub-period-select"></select>
        </div>
        <div id="stub-content" style="background:#fff;color:#111;border-radius:8px;padding:24px;font-family:monospace;font-size:13px;line-height:1.8"></div>
      </div>
    </div>

    <!-- ── Quarter/Year Summary Modal ────────────────────────────────────── -->
    <div id="summary-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.7);z-index:1001;align-items:flex-start;justify-content:center;padding:32px 16px;overflow-y:auto">
      <div class="card" style="width:700px;margin-bottom:40px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
          <h3 id="summary-title" style="font-size:15px;font-weight:700"></h3>
          <div style="display:flex;gap:8px">
            <button class="btn btn-ghost btn-sm" id="summary-print-btn"><i class="bi bi-printer"></i> Print</button>
            <button class="btn btn-ghost btn-sm" id="summary-close-btn"><i class="bi bi-x"></i></button>
          </div>
        </div>
        <div id="summary-content"></div>
      </div>
    </div>

    <!-- ── Payroll History ────────────────────────────────────────────────── -->
    <div id="payroll-content"><div class="empty-state"><div class="spinner"></div></div></div>
  `;

  // Load employees
  const api = getAPI();
  let employees: Employee[] = [];
  try { employees = (await api.getEmployees(businessId)) as Employee[]; } catch { /* preview */ }

  const empSelect = document.getElementById('pay-employee-id') as HTMLSelectElement;
  employees.forEach((e: Employee) => {
    const opt = document.createElement('option');
    opt.value = e.id;
    opt.textContent = `${e.name} — ${e.role} ($${e.hourlyRate ?? 0}/hr)`;
    opt.dataset['rate'] = String(e.hourlyRate ?? 0);
    empSelect.appendChild(opt);
  });

  // Bank wire status
  try {
    const banking = JSON.parse(localStorage.getItem('preuve_banking') ?? '{}') as { bankName?: string; achEnabled?: boolean };
    const el = document.getElementById('bank-wire-status')!;
    if (banking.achEnabled && banking.bankName) {
      el.innerHTML = `<span style="color:var(--success)"> ✓ ACH enabled via ${banking.bankName}</span>`;
    } else {
      el.innerHTML = ` Configure bank in <strong>Settings → Banking</strong> to enable auto-wire.`;
    }
  } catch { /* */ }

  // Auto-fill rate when employee changes
  empSelect.addEventListener('change', () => {
    const opt = empSelect.selectedOptions[0];
    if (opt?.dataset['rate']) {
      (document.getElementById('pay-rate') as HTMLInputElement).value = opt.dataset['rate'];
      updateLivePreview(hasTips);
    }
  });

  // Live preview on any input change
  const liveInputIds = ['pay-hours', 'pay-ot-hours', 'pay-rate', 'pay-ytd', 'pay-period-type', ...(hasTips ? ['pay-tips'] : [])];
  const updateHandler = () => updateLivePreview(hasTips);
  liveInputIds.forEach((id) => {
    document.getElementById(id)?.addEventListener('input', updateHandler);
    document.getElementById(id)?.addEventListener('change', updateHandler);
  });

  // Open payroll modal
  document.getElementById('run-payroll-btn')!.addEventListener('click', () => {
    const today = new Date();
    const start = new Date(today); start.setDate(today.getDate() - 14);
    (document.getElementById('pay-period-start') as HTMLInputElement).value = start.toISOString().slice(0, 10);
    (document.getElementById('pay-period-end')   as HTMLInputElement).value = new Date(today.setDate(today.getDate() - 1)).toISOString().slice(0, 10);
    resetLivePreview();
    document.getElementById('payroll-modal')!.style.display = 'flex';
  });

  document.getElementById('close-payroll-modal')!.addEventListener('click', () => {
    document.getElementById('payroll-modal')!.style.display = 'none';
  });

  // Process payroll
  document.getElementById('save-payroll-btn')!.addEventListener('click', async () => {
    const employeeId = empSelect.value;
    const empName    = empSelect.selectedOptions[0]?.text?.split(' —')[0] ?? '';
    if (!employeeId) { showToast('Select an employee', 'error'); return; }

    const taxes = computeFromForm(hasTips);
    if (!taxes) return;

    const periodStart = (document.getElementById('pay-period-start') as HTMLInputElement).value;
    const periodEnd   = (document.getElementById('pay-period-end')   as HTMLInputElement).value;
    const hours   = parseFloat((document.getElementById('pay-hours')    as HTMLInputElement).value);
    const otHours = parseFloat((document.getElementById('pay-ot-hours') as HTMLInputElement).value) || 0;
    const tips    = hasTips ? (parseFloat((document.getElementById('pay-tips') as HTMLInputElement)?.value ?? '0') || 0) : 0;
    const rate    = parseFloat((document.getElementById('pay-rate') as HTMLInputElement).value);

    const record = {
      businessId, employeeId, employeeName: empName, periodStart, periodEnd,
      hoursWorked: hours, overtimeHours: otHours, hourlyRate: rate,
      grossPay: taxes.grossPay, tips,
      federalIncomeTax: taxes.federalIncomeTax, socialSecurity: taxes.socialSecurity,
      medicare: taxes.medicare, waPfml: taxes.waPfml, totalDeductions: taxes.totalDeductions,
      netPay: taxes.netPay, employerSS: taxes.employerSS,
      employerMedicare: taxes.employerMedicare, employerPfml: taxes.employerPfml,
      status: 'processed' as const,
    };

    try {
      await api.createPayrollRecord(record);
      showToast(`Payroll processed — Net $${taxes.netPay.toFixed(2)} for ${empName}`, 'success');
    } catch {
      showToast('Payroll saved (preview mode)', 'info');
    }
    document.getElementById('payroll-modal')!.style.display = 'none';
    await loadHistory(businessId);
  });

  // Stub modal close
  document.getElementById('stub-close-btn')!.addEventListener('click', () => {
    document.getElementById('stub-modal')!.style.display = 'none';
  });

  // Summary modal close
  document.getElementById('summary-close-btn')!.addEventListener('click', () => {
    document.getElementById('summary-modal')!.style.display = 'none';
  });

  // Close Quarter
  document.getElementById('close-quarter-btn')!.addEventListener('click', async () => {
    const records = await getRecords(businessId);
    const now = new Date();
    const q = Math.floor(now.getMonth() / 3) + 1;
    const year = now.getFullYear();
    // Get current quarter's records
    const qStart = new Date(year, (q - 1) * 3, 1);
    const qEnd   = new Date(year, q * 3, 0);
    const qRecords = records.filter((r) => {
      const d = new Date(r.periodEnd ?? r.processedAt);
      return d >= qStart && d <= qEnd;
    });
    renderSummaryModal(`Q${q} ${year} Payroll Summary`, qRecords, `Q${q} ${year}`);
  });

  // Year-End Close
  document.getElementById('close-year-btn')!.addEventListener('click', async () => {
    const records = await getRecords(businessId);
    const year = new Date().getFullYear();
    const yRecords = records.filter((r) => {
      const d = new Date(r.periodEnd ?? r.processedAt);
      return d.getFullYear() === year;
    });
    renderSummaryModal(`${year} Annual Payroll Summary (W-2 Preview)`, yRecords, String(year));
  });

  await loadHistory(businessId);
}

// ── Live Preview ──────────────────────────────────────────────────────────────

function computeFromForm(hasTips: boolean): TaxCalc | null {
  const hours   = parseFloat((document.getElementById('pay-hours')       as HTMLInputElement).value);
  const otHours = parseFloat((document.getElementById('pay-ot-hours')    as HTMLInputElement).value) || 0;
  const rate    = parseFloat((document.getElementById('pay-rate')        as HTMLInputElement).value);
  const ytd     = parseFloat((document.getElementById('pay-ytd')        as HTMLInputElement).value) || 0;
  const periods = parseInt((document.getElementById('pay-period-type')   as HTMLSelectElement).value);
  const tips    = hasTips ? (parseFloat((document.getElementById('pay-tips') as HTMLInputElement)?.value ?? '0') || 0) : 0;

  if (isNaN(hours) || isNaN(rate) || hours <= 0 || rate <= 0) {
    showToast('Enter hours and hourly rate to calculate', 'error');
    return null;
  }
  const grossPay = hours * rate + otHours * rate * 1.5 + tips;
  return calculateTaxes(grossPay, ytd, periods);
}

function updateLivePreview(hasTips: boolean): void {
  const hours   = parseFloat((document.getElementById('pay-hours')    as HTMLInputElement)?.value) || 0;
  const otHours = parseFloat((document.getElementById('pay-ot-hours') as HTMLInputElement)?.value) || 0;
  const rate    = parseFloat((document.getElementById('pay-rate')     as HTMLInputElement)?.value) || 0;
  const ytd     = parseFloat((document.getElementById('pay-ytd')     as HTMLInputElement)?.value) || 0;
  const periods = parseInt((document.getElementById('pay-period-type') as HTMLSelectElement)?.value) || 26;
  const tips    = hasTips ? (parseFloat((document.getElementById('pay-tips') as HTMLInputElement)?.value ?? '0') || 0) : 0;

  if (!hours || !rate) { resetLivePreview(); return; }

  const reg  = hours * rate;
  const ot   = otHours * rate * 1.5;
  const gross = reg + ot + tips;
  const t    = calculateTaxes(gross, ytd, periods);
  const f    = (n: number) => `$${n.toFixed(2)}`;

  setText('prev-reg',      f(reg));
  setText('prev-ot',       f(ot));
  if (hasTips) setText('prev-tips-amt', f(tips));
  setText('prev-gross',    f(gross));
  setText('prev-fed',      `-${f(t.federalIncomeTax)}`);
  setText('prev-ss',       `-${f(t.socialSecurity)}`);
  setText('prev-med',      `-${f(t.medicare)}`);
  setText('prev-pfml',     `-${f(t.waPfml)}`);
  setText('prev-net',      f(t.netPay));
  setText('prev-emp-ss',   t.employerSS.toFixed(2));
  setText('prev-emp-med',  t.employerMedicare.toFixed(2));
  setText('prev-emp-pfml', t.employerPfml.toFixed(2));
  setText('prev-emp-total',t.employerTotal.toFixed(2));
  const notice = document.getElementById('preview-notice');
  if (notice) notice.textContent = '';

  // Color the net pay
  const netEl = document.getElementById('prev-net');
  if (netEl) netEl.style.color = 'var(--success)';
}

function resetLivePreview(): void {
  ['prev-reg','prev-ot','prev-tips-amt','prev-gross','prev-fed','prev-ss','prev-med','prev-pfml','prev-net',
   'prev-emp-ss','prev-emp-med','prev-emp-pfml','prev-emp-total'].forEach((id) => setText(id, '—'));
  const notice = document.getElementById('preview-notice');
  if (notice) notice.textContent = '(fill in hours and rate to calculate)';
}

function setText(id: string, val: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = val;
}

// ── History ───────────────────────────────────────────────────────────────────

async function getRecords(businessId: string): Promise<PayrollRecord[]> {
  try {
    const records = (await getAPI().getPayrollRecords(businessId)) as PayrollRecord[];
    if (records.length) return records;
  } catch { /* */ }
  return getSamplePayrollRecords(businessId);
}

async function loadHistory(businessId: string): Promise<void> {
  const container = document.getElementById('payroll-content');
  if (!container) return;

  const records = await getRecords(businessId);

  const byEmployee = new Map<string, PayrollRecord[]>();
  records.forEach((r) => {
    if (!byEmployee.has(r.employeeId)) byEmployee.set(r.employeeId, []);
    byEmployee.get(r.employeeId)!.push(r);
  });

  const totalNet    = records.reduce((s, r) => s + r.netPay,           0);
  const totalGross  = records.reduce((s, r) => s + r.grossPay,         0);
  const totalTax    = records.reduce((s, r) => s + r.totalDeductions,  0);
  const empTaxTotal = records.reduce((s, r) => s + r.employerSS + r.employerMedicare + r.employerPfml, 0);

  let html = `
    <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:20px">
      ${statCard('Total Gross Paid',     `$${totalGross.toFixed(2)}`,  'bi-cash-coin',            'var(--brand-gold)')}
      ${statCard('Total Net Paid',       `$${totalNet.toFixed(2)}`,    'bi-arrow-up-right-circle', 'var(--success)')}
      ${statCard('Employee Deductions',  `$${totalTax.toFixed(2)}`,    'bi-arrow-down-right-circle','var(--danger)')}
      ${statCard('Employer Taxes',       `$${empTaxTotal.toFixed(2)}`, 'bi-bank',                 'var(--brand-blue-light)')}
    </div>
  `;

  byEmployee.forEach((empRecords) => {
    const empName = empRecords[0].employeeName;
    const empNet  = empRecords.reduce((s, r) => s + r.netPay, 0);
    html += `
      <div class="card" style="margin-bottom:16px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <div style="font-weight:700;font-size:14px">${esc(empName)}</div>
          <div style="display:flex;gap:10px;align-items:center">
            <div style="font-size:13px;color:var(--text-muted)">YTD Net: <strong style="color:var(--text-primary)">$${empNet.toFixed(2)}</strong></div>
            <button class="btn btn-ghost btn-sm" data-stub-emp="${esc(empRecords[0].employeeId)}" style="font-size:11px">
              <i class="bi bi-printer"></i> Print Stub
            </button>
          </div>
        </div>
        <div style="overflow-x:auto">
          <table style="width:100%;border-collapse:collapse;font-size:12px">
            <thead>
              <tr style="border-bottom:1px solid var(--border);color:var(--text-muted)">
                <th style="padding:6px 8px;text-align:left">Period</th>
                <th style="padding:6px 8px;text-align:right">Hours</th>
                <th style="padding:6px 8px;text-align:right">Gross</th>
                <th style="padding:6px 8px;text-align:right">Fed Tax</th>
                <th style="padding:6px 8px;text-align:right">SS+Med</th>
                <th style="padding:6px 8px;text-align:right">PFML</th>
                <th style="padding:6px 8px;text-align:right">Net Pay</th>
                <th style="padding:6px 8px"></th>
              </tr>
            </thead>
            <tbody>
              ${empRecords.map((r) => `
                <tr style="border-bottom:1px solid var(--border)22">
                  <td style="padding:7px 8px;color:var(--text-muted)">${r.periodStart?.slice(0,10)} – ${r.periodEnd?.slice(0,10)}</td>
                  <td style="padding:7px 8px;text-align:right">${r.hoursWorked}${r.overtimeHours ? ` +${r.overtimeHours}OT` : ''}</td>
                  <td style="padding:7px 8px;text-align:right;font-weight:600">$${r.grossPay.toFixed(2)}</td>
                  <td style="padding:7px 8px;text-align:right;color:var(--danger)">-$${r.federalIncomeTax.toFixed(2)}</td>
                  <td style="padding:7px 8px;text-align:right;color:var(--danger)">-$${(r.socialSecurity + r.medicare).toFixed(2)}</td>
                  <td style="padding:7px 8px;text-align:right;color:var(--danger)">-$${r.waPfml.toFixed(2)}</td>
                  <td style="padding:7px 8px;text-align:right;font-weight:700;color:var(--success)">$${r.netPay.toFixed(2)}</td>
                  <td style="padding:7px 8px;white-space:nowrap">
                    <button class="btn btn-ghost" style="font-size:11px;padding:3px 8px" data-delete-id="${r.id}">
                      <i class="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>`).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  });

  container.innerHTML = html;

  // Print stub — per employee (opens period-chooser modal)
  container.querySelectorAll('[data-stub-emp]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const empId = (btn as HTMLElement).dataset['stubEmp']!;
      const empRecs = byEmployee.get(empId) ?? [];
      openStubModal(empRecs);
    });
  });

  // Delete
  container.querySelectorAll('[data-delete-id]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this payroll record?')) return;
      const id = (btn as HTMLElement).dataset['deleteId']!;
      try { await getAPI().deletePayrollRecord(id); } catch { /* preview */ }
      showToast('Record deleted', 'info');
      await loadHistory(businessId);
    });
  });
}

// ── Stub Modal ────────────────────────────────────────────────────────────────

function openStubModal(records: PayrollRecord[]): void {
  if (!records.length) return;
  const sel = document.getElementById('stub-period-select') as HTMLSelectElement;
  sel.innerHTML = records.map((r, i) =>
    `<option value="${i}">${r.periodStart?.slice(0,10)} – ${r.periodEnd?.slice(0,10)}</option>`
  ).join('');

  const render = () => {
    const idx = parseInt(sel.value) || 0;
    const r = records[idx];
    if (r) renderStubContent(r);
  };
  sel.onchange = render;
  render();

  document.getElementById('stub-print-btn')!.onclick = () => {
    const content = document.getElementById('stub-content')!.innerHTML;
    const win = window.open('', '_blank', 'width=520,height=680')!;
    win.document.write(`<!DOCTYPE html><html><head><title>Pay Stub</title>
      <style>body{font-family:monospace;font-size:13px;color:#111;padding:24px}
      table{width:100%;border-collapse:collapse}td{padding:5px 8px}
      .sep td{border-top:1px solid #ccc;padding-top:8px}
      .total td{font-weight:bold;font-size:15px;padding-top:8px}</style>
      </head><body>${content}</body></html>`);
    win.document.close();
    setTimeout(() => { try { win.print(); } catch { /* */ } }, 400);
  };

  document.getElementById('stub-modal')!.style.display = 'flex';
}

function renderStubContent(r: PayrollRecord): void {
  const f = (n: number) => `$${n.toFixed(2)}`;
  document.getElementById('stub-content')!.innerHTML = `
    <h2 style="text-align:center;margin-bottom:4px;font-size:16px">PAY STUB</h2>
    <p style="text-align:center;margin-bottom:16px;font-size:12px;color:#555">
      ${esc(r.employeeName)}<br>
      Period: ${r.periodStart?.slice(0,10)} – ${r.periodEnd?.slice(0,10)}<br>
      Processed: ${r.processedAt?.slice(0,10) ?? '—'}
    </p>
    <table>
      <tr><td>Regular Hours</td><td align="right">${r.hoursWorked}</td></tr>
      ${r.overtimeHours ? `<tr><td>Overtime Hours (1.5×)</td><td align="right">${r.overtimeHours}</td></tr>` : ''}
      <tr><td>Hourly Rate</td><td align="right">${f(r.hourlyRate)}</td></tr>
      ${(r.tips ?? 0) > 0 ? `<tr><td>Tips</td><td align="right">${f(r.tips!)}</td></tr>` : ''}
      <tr class="sep"><td><strong>Gross Pay</strong></td><td align="right"><strong>${f(r.grossPay)}</strong></td></tr>
      <tr><td>Federal Income Tax</td><td align="right">-${f(r.federalIncomeTax)}</td></tr>
      <tr><td>Social Security (6.2%)</td><td align="right">-${f(r.socialSecurity)}</td></tr>
      <tr><td>Medicare (1.45%)</td><td align="right">-${f(r.medicare)}</td></tr>
      <tr><td>WA PFML (0.67%)</td><td align="right">-${f(r.waPfml)}</td></tr>
      <tr><td>Total Deductions</td><td align="right">-${f(r.totalDeductions)}</td></tr>
      <tr class="total"><td>NET PAY</td><td align="right">${f(r.netPay)}</td></tr>
    </table>
    <p style="margin-top:20px;font-size:10px;color:#888;text-align:center">
      Washington State: No state income tax · Bellingham: No city payroll tax<br>
      Employer cost this period: SS ${f(r.employerSS)} · Medicare ${f(r.employerMedicare)} · PFML ${f(r.employerPfml)}
    </p>
  `;
}

// ── Quarter / Year Summary ────────────────────────────────────────────────────

function renderSummaryModal(title: string, records: PayrollRecord[], period: string): void {
  document.getElementById('summary-title')!.textContent = title;

  if (!records.length) {
    document.getElementById('summary-content')!.innerHTML =
      `<div class="empty-state" style="padding:40px 0"><div style="font-size:13px;color:var(--text-muted)">No payroll records found for ${period}.</div></div>`;
    document.getElementById('summary-modal')!.style.display = 'flex';
    return;
  }

  // Aggregate per employee
  const byEmp = new Map<string, { name: string; gross: number; fedTax: number; ss: number; med: number; pfml: number; net: number; empSS: number; empMed: number; empPfml: number }>();
  records.forEach((r) => {
    if (!byEmp.has(r.employeeId)) byEmp.set(r.employeeId, { name: r.employeeName, gross: 0, fedTax: 0, ss: 0, med: 0, pfml: 0, net: 0, empSS: 0, empMed: 0, empPfml: 0 });
    const e = byEmp.get(r.employeeId)!;
    e.gross   += r.grossPay;        e.fedTax += r.federalIncomeTax;
    e.ss      += r.socialSecurity;  e.med    += r.medicare;
    e.pfml    += r.waPfml;          e.net    += r.netPay;
    e.empSS   += r.employerSS;      e.empMed += r.employerMedicare;
    e.empPfml += r.employerPfml;
  });

  const totals = [...byEmp.values()].reduce((t, e) => ({
    gross: t.gross + e.gross, fedTax: t.fedTax + e.fedTax, ss: t.ss + e.ss,
    med: t.med + e.med, pfml: t.pfml + e.pfml, net: t.net + e.net,
    empSS: t.empSS + e.empSS, empMed: t.empMed + e.empMed, empPfml: t.empPfml + e.empPfml,
    name: '', gross2: 0, fedTax2: 0, ss2: 0, med2: 0, pfml2: 0, net2: 0, empSS2: 0, empMed2: 0, empPfml2: 0,
  }), { gross: 0, fedTax: 0, ss: 0, med: 0, pfml: 0, net: 0, empSS: 0, empMed: 0, empPfml: 0, name: '', gross2: 0, fedTax2: 0, ss2: 0, med2: 0, pfml2: 0, net2: 0, empSS2: 0, empMed2: 0, empPfml2: 0 });

  const f = (n: number) => `$${n.toFixed(2)}`;
  const isAnnual = title.includes('Annual') || title.includes('W-2');

  document.getElementById('summary-content')!.innerHTML = `
    ${isAnnual ? `<div style="background:var(--bg-tertiary);border-radius:8px;padding:12px 16px;margin-bottom:16px;font-size:12px;color:var(--text-muted)">
      <strong style="color:var(--brand-gold)">W-2 Summary Preview</strong> — Use these totals when completing W-2 forms.
      Box 1: Gross Wages · Box 2: Federal Tax Withheld · Box 4: SS Tax · Box 6: Medicare Tax · Box 16: WA Wages (same as Box 1, no WA income tax)
    </div>` : ''}
    <div style="overflow-x:auto">
      <table style="width:100%;border-collapse:collapse;font-size:12px">
        <thead>
          <tr style="background:var(--bg-tertiary);color:var(--text-muted);font-size:11px;text-transform:uppercase;letter-spacing:.5px">
            <th style="padding:8px 10px;text-align:left">Employee</th>
            <th style="padding:8px 10px;text-align:right">Gross${isAnnual ? ' (Box 1)' : ''}</th>
            <th style="padding:8px 10px;text-align:right">Fed Tax${isAnnual ? ' (Box 2)' : ''}</th>
            <th style="padding:8px 10px;text-align:right">SS Tax${isAnnual ? ' (Box 4)' : ''}</th>
            <th style="padding:8px 10px;text-align:right">Medicare${isAnnual ? ' (Box 6)' : ''}</th>
            <th style="padding:8px 10px;text-align:right">WA PFML</th>
            <th style="padding:8px 10px;text-align:right">Net Paid</th>
            <th style="padding:8px 10px;text-align:right">Emp. Cost</th>
          </tr>
        </thead>
        <tbody>
          ${[...byEmp.values()].map((e) => `
            <tr style="border-bottom:1px solid var(--border)22">
              <td style="padding:8px 10px;font-weight:600">${esc(e.name)}</td>
              <td style="padding:8px 10px;text-align:right">${f(e.gross)}</td>
              <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(e.fedTax)}</td>
              <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(e.ss)}</td>
              <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(e.med)}</td>
              <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(e.pfml)}</td>
              <td style="padding:8px 10px;text-align:right;font-weight:600;color:var(--success)">${f(e.net)}</td>
              <td style="padding:8px 10px;text-align:right;color:var(--text-muted)">${f(e.empSS + e.empMed + e.empPfml)}</td>
            </tr>`).join('')}
        </tbody>
        <tfoot>
          <tr style="border-top:2px solid var(--border);font-weight:700">
            <td style="padding:8px 10px">TOTAL</td>
            <td style="padding:8px 10px;text-align:right">${f(totals.gross)}</td>
            <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(totals.fedTax)}</td>
            <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(totals.ss)}</td>
            <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(totals.med)}</td>
            <td style="padding:8px 10px;text-align:right;color:var(--danger)">${f(totals.pfml)}</td>
            <td style="padding:8px 10px;text-align:right;color:var(--success)">${f(totals.net)}</td>
            <td style="padding:8px 10px;text-align:right">${f(totals.empSS + totals.empMed + totals.empPfml)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    <div style="margin-top:16px;font-size:11px;color:var(--text-muted);line-height:1.6">
      WA State: No state income tax (no Box 17) · Bellingham: No city payroll tax ·
      Employer 941 liability: SS ${f(totals.empSS)} + Medicare ${f(totals.empMed)} + PFML ${f(totals.empPfml)}
      = <strong style="color:var(--text-secondary)">${f(totals.empSS + totals.empMed + totals.empPfml)}</strong>
    </div>
  `;

  document.getElementById('summary-print-btn')!.onclick = () => {
    const content = document.getElementById('summary-content')!.innerHTML;
    const win = window.open('', '_blank', 'width=760,height=640')!;
    win.document.write(`<!DOCTYPE html><html><head><title>${title}</title>
      <style>body{font-family:sans-serif;font-size:12px;color:#111;padding:24px}
      table{width:100%;border-collapse:collapse}th,td{padding:6px 8px;border-bottom:1px solid #ddd;text-align:right}
      th:first-child,td:first-child{text-align:left}tfoot tr{font-weight:bold;border-top:2px solid #999}</style>
      </head><body><h2>${title}</h2>${content}</body></html>`);
    win.document.close();
    setTimeout(() => { try { win.print(); } catch { /* */ } }, 400);
  };

  document.getElementById('summary-modal')!.style.display = 'flex';
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function statCard(label: string, value: string, icon: string, color: string): string {
  return `
    <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:10px;padding:16px">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
        <i class="bi ${icon}" style="color:${color};font-size:16px"></i>
        <span style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.5px">${label}</span>
      </div>
      <div style="font-size:20px;font-weight:700;color:var(--text-primary)">${value}</div>
    </div>`;
}

function getSamplePayrollRecords(businessId: string): PayrollRecord[] {
  const period = (weeksAgo: number) => {
    const end = new Date(); end.setDate(end.getDate() - weeksAgo * 14);
    const start = new Date(end); start.setDate(end.getDate() - 13);
    return { periodStart: start.toISOString().slice(0,10), periodEnd: end.toISOString().slice(0,10) };
  };
  const processed = (weeksAgo: number) => { const d = new Date(); d.setDate(d.getDate() - weeksAgo * 14); return d.toISOString(); };
  const make = (id: string, empId: string, empName: string, hours: number, ot: number, rate: number, tips: number, ytd: number, weeksAgo: number): PayrollRecord => {
    const gross = hours * rate + ot * rate * 1.5 + tips;
    const taxes = calculateTaxes(gross, ytd, 26);
    return { id, businessId, employeeId: empId, employeeName: empName, ...period(weeksAgo),
      hoursWorked: hours, overtimeHours: ot, hourlyRate: rate, grossPay: gross, tips,
      federalIncomeTax: taxes.federalIncomeTax, socialSecurity: taxes.socialSecurity,
      medicare: taxes.medicare, waPfml: taxes.waPfml, totalDeductions: taxes.totalDeductions,
      netPay: taxes.netPay, employerSS: taxes.employerSS, employerMedicare: taxes.employerMedicare,
      employerPfml: taxes.employerPfml, status: 'processed', processedAt: processed(weeksAgo) };
  };
  return [
    make('sp1','e1','Alex Johnson',  80,0, 16.66,218.50,   0,  1),
    make('sp2','e1','Alex Johnson',  80,4, 16.66,195.75,1332,  2),
    make('sp3','e1','Alex Johnson',  80,0, 16.66,241.00,2664,  3),
    make('sp4','e2','Maria Santos',  80,6, 16.66,310.40,   0,  1),
    make('sp5','e2','Maria Santos',  80,0, 16.66,288.20,1466,  2),
    make('sp6','e2','Maria Santos',  76,0, 16.66,276.80,2799,  3),
    make('sp7','e3','Derek Lee',     80,8, 18.00,  0,      0,  1),
    make('sp8','e3','Derek Lee',     80,0, 18.00,  0,   1680,  2),
    make('sp9','e4','Sandra Kim',    80,0, 22.00,  0,      0,  1),
    make('sp10','e4','Sandra Kim',   80,0, 22.00,  0,   3520,  2),
  ];
}

function esc(s: string): string {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
